import { useState } from 'react';
import { ApiRequestError } from '../api/client';
import { api } from '../api/shop';
import { useAuth } from '../auth/AuthContext';

/**
 * Set or change the password on this account.
 *
 * Two shapes, one form. A shopper who signed in with a code has no password to
 * confirm, so asking for one would be a dead end they cannot get out of; a shopper
 * who has one must confirm it, because an unlocked phone left on a table is
 * otherwise a free account takeover.
 *
 * `hasPassword` comes from the session rather than a lookup — it is a fact the
 * server already returns about the caller's own account, and a separate endpoint
 * for it would be one more thing to keep in step.
 *
 * SETTING A PASSWORD ENDS EVERY OTHER SESSION, including the one that asked. The
 * server returns a fresh token pair for exactly that reason, and feeding it back
 * into signIn() is what stops the customer being signed out by their own action.
 */

/** Mirrors PasswordPolicy.MinimumLength on the server. */
const MIN_PASSWORD_LENGTH = 12;

export function PasswordSettings() {
  const { hasPassword, signIn } = useAuth();

  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function reset() {
    setCurrentPassword('');
    setNewPassword('');
    setError(null);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setDone(false);

    try {
      const tokens = await api.setPassword(
        newPassword,
        hasPassword ? currentPassword : undefined,
      );

      // Adopts the new session before clearing state: the old refresh token has
      // already been revoked server-side, so anything that read it after this point
      // would be signing in with a dead credential.
      signIn(tokens);

      reset();
      setOpen(false);
      setDone(true);
    } catch (ex) {
      setError(
        ex instanceof ApiRequestError ? ex.message : 'Could not save that. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="stack" style={{ gap: 10 }}>
      <h2>How you sign in</h2>

      {hasPassword ? (
        <p>
          You sign in with your email address and a password. You can also ask for a
          one-time code at any time — that is how you get back in if you forget the
          password, so there is nothing to reset and no reset link to wait for.
        </p>
      ) : (
        <p>
          There is no password on your account. Each time you sign in we send a
          one-time code to your email address, valid for a few minutes. You can set a
          password if you would rather not wait for a code.
        </p>
      )}

      {/* role="status" so the confirmation is announced rather than only seen. */}
      {done && (
        <p className="notice" role="status">
          Your password is saved. Any other devices you were signed in on have been
          signed out.
        </p>
      )}

      {!open ? (
        <button
          type="button"
          className="btn secondary"
          onClick={() => {
            reset();
            setDone(false);
            setOpen(true);
          }}
        >
          {hasPassword ? 'Change my password' : 'Set a password'}
        </button>
      ) : (
        <form className="stack" style={{ gap: 16 }} onSubmit={(e) => void submit(e)}>
          {hasPassword && (
            <div className="field">
              <label htmlFor="current-password">Current password</label>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
          )}

          <div className="field">
            <label htmlFor="account-new-password">New password</label>
            <input
              id="account-new-password"
              type="password"
              /* new-password is what tells a password manager to offer to generate
                 and save one, rather than autofilling the old one. */
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              maxLength={200}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              aria-describedby="account-password-hint"
              aria-invalid={error !== null}
            />
            <span className="hint" id="account-password-hint">
              At least {MIN_PASSWORD_LENGTH} characters. Length matters more than
              symbols — a few ordinary words work well.
            </span>
          </div>

          {error !== null && (
            <p className="notice error" role="alert">
              {error}
            </p>
          )}

          <p className="hint">
            Saving this signs you out everywhere else. You will stay signed in here.
          </p>

          <div className="row" style={{ gap: 10 }}>
            <button
              type="submit"
              className="btn"
              disabled={
                busy
                || newPassword.length < MIN_PASSWORD_LENGTH
                || (hasPassword && currentPassword === '')
              }
            >
              {busy ? 'Saving…' : 'Save password'}
            </button>

            <button
              type="button"
              className="btn secondary"
              onClick={() => {
                reset();
                setOpen(false);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
